import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Card, CardContent, CircularProgress,
  Snackbar, Alert, FormControl, InputLabel, Select, MenuItem, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination,
  Checkbox, FormControlLabel, useTheme, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import AnalyticsExportDialog, { DatasetKey } from '../components/analytics/AnalyticsExportDialog';
import AffiliatePayoutsSection from '../components/analytics/AffiliatePayoutsSection';
import { isAuthenticatedSuperAdmin } from '../api/auth';

interface AnalyticsDashboardPageProps {
  tenantId?: number;
}

type DateRangePreset = 'today' | 'yesterday' | 'last7' | 'lastWeek' | 'month' | 'custom';
type ActivityFilter = 'all' | 'purchases' | 'redeems';

interface TimeSeriesPoint {
  date: string;
  signups: number;
  purchases: number;
  redeems: number;
  ggr: number;
}

interface SummaryTotals {
  total_purchases: number;
  total_redeems: number;
  total_ggr: number;
  distinct_purchasers: number;
  distinct_redeemers: number;
}

interface PlayerRow {
  user_id: number;
  email: string;
  username?: string | null;
  affiliate_id: string | null;
  purchases: number;
  purchase_count?: number;
  first_purchase_at?: string | null;
  last_purchase_at?: string | null;
  redeems: number;
  individual_ggr: number;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
}

interface SignupRow {
  id: number;
  email: string;
  affiliate_id: string | null;
  created_at: string;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
}

interface AffiliateGroup {
  affiliate_id: string | null;
  player_count: number;
  total_purchases: number;
  total_redeems: number;
  net_ggr: number;
  affiliate_ggr: number;
}

interface AffiliatePlayerRow {
  affiliate_id: string | null;
  user_id: number;
  email: string;
  purchases: number;
  redeems: number;
  net_ggr: number;
}

interface InactivePlayerRow {
  user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  affiliate_id: string | null;
  signed_up_at: string | null;
  last_purchase_at: string | null;
  days_since_purchase: number | null;
  lifetime_purchase_count: number;
  lifetime_purchases: number;
  lifetime_redeems: number;
  lifetime_ggr: number;
  credit_balance: number;
}

interface PaginationInfo {
  total_pages: number;
  total_items: number;
}

const PAGE_SIZE = 50;
const INACTIVE_PAGE_SIZE = 100;
const INACTIVE_ADDON_KEY = 'inactive_players_report';
const INACTIVE_TAB = 3;
const PAYOUTS_TAB = 4;

// Returns the first argument that is actually an array, else []. Guards against
// unexpected API response shapes (error objects, HTML, wrapped payloads) that would
// otherwise crash recharts/tables when passed a non-array.
const asArray = <T,>(...candidates: any[]): T[] => {
  for (const c of candidates) {
    if (Array.isArray(c)) return c as T[];
  }
  return [];
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount || 0);

const formatEst = (iso: string | null | undefined) => {
  if (!iso) return '';
  const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: 'short', day: '2-digit',
      hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
    }).format(new Date(normalized));
  } catch {
    return new Date(iso).toString();
  }
};

const fullName = (first?: string | null, last?: string | null) =>
  [first, last].filter(Boolean).join(' ') || '—';

const toDateParam = (d: Date) => {
  // Local calendar date as YYYY-MM-DD (avoids UTC shift from toISOString()).
  // Used only for Custom Range dates, which are picked tz-independently.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const TIMEZONE_OPTIONS: { value: string; label: string; abbr: string }[] = [
  { value: 'America/New_York', label: 'Eastern Time', abbr: 'ET' },
  { value: 'America/Chicago', label: 'Central Time', abbr: 'CT' },
  { value: 'America/Denver', label: 'Mountain Time', abbr: 'MT' },
  { value: 'America/Phoenix', label: 'Arizona Time', abbr: 'AZ' },
  { value: 'America/Los_Angeles', label: 'Pacific Time', abbr: 'PT' },
  { value: 'UTC', label: 'UTC', abbr: 'UTC' },
];
const TZ_STORAGE_KEY = 'analyticsTimezone';
const DEFAULT_TZ = 'America/Chicago';

const getInitialTimezone = (): string => {
  try {
    const stored = localStorage.getItem(TZ_STORAGE_KEY);
    if (stored && TIMEZONE_OPTIONS.some((o) => o.value === stored)) return stored;
  } catch { /* localStorage unavailable (private mode, quota, etc.) */ }
  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (TIMEZONE_OPTIONS.some((o) => o.value === browserTz)) return browserTz;
  } catch { /* ignore */ }
  return DEFAULT_TZ;
};

// Calendar dates (YYYY-MM-DD) handled as UTC midnights so no local offset shifts the day.
const parseDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
const toDay = (d: Date) => d.toISOString().slice(0, 10);

const todayInTz = (tz: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

// Quick-range dates computed relative to "today" in the selected report timezone.
const computeRange = (preset: DateRangePreset, tz: string): { start: string; end: string } | null => {
  const today = parseDay(todayInTz(tz));
  switch (preset) {
    case 'today': return { start: toDay(today), end: toDay(today) };
    case 'yesterday': { const y = addDays(today, -1); return { start: toDay(y), end: toDay(y) }; }
    case 'last7': return { start: toDay(addDays(today, -6)), end: toDay(today) };
    case 'lastWeek': {
      const dow = today.getUTCDay();
      const thisMonday = addDays(today, -((dow + 6) % 7));
      const lastMonday = addDays(thisMonday, -7);
      return { start: toDay(lastMonday), end: toDay(addDays(lastMonday, 6)) };
    }
    case 'month': {
      const [y, m] = todayInTz(tz).split('-');
      return { start: `${y}-${m}-01`, end: toDay(today) };
    }
    case 'custom':
      return null;
  }
};

const formatInTz = (iso: string | null | undefined, tz: string) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: 'short', day: '2-digit',
      hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const AnalyticsDashboardPage: React.FC<AnalyticsDashboardPageProps> = ({ tenantId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isSuperAdminView = tenantId === undefined;

  // ----- Controls -----
  const [dateRange, setDateRange] = useState<DateRangePreset>('last7');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [tenantFilter, setTenantFilter] = useState('');
  const [timezone, setTimezone] = useState<string>(getInitialTimezone);
  const [activity, setActivity] = useState<ActivityFilter>('all');

  // Committed values actually used for fetching (applied on "Apply").
  const [applied, setApplied] = useState<{ start: string; end: string; tenant?: number } | null>(null);

  const [exportOpen, setExportOpen] = useState(false);
  const [exportDataset, setExportDataset] = useState<DatasetKey>('signups');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'error' },
  );
  const showError = useCallback((message: string) => setSnackbar({ open: true, message, severity: 'error' }), []);

  // ----- Data -----
  const [totals, setTotals] = useState<SummaryTotals | null>(null);
  const [series, setSeries] = useState<TimeSeriesPoint[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(false);

  const [tab, setTab] = useState(0);

  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [playersPage, setPlayersPage] = useState(1);
  const [playersPagination, setPlayersPagination] = useState<PaginationInfo>({ total_pages: 1, total_items: 0 });
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  const [signups, setSignups] = useState<SignupRow[]>([]);
  const [signupsPage, setSignupsPage] = useState(1);
  const [signupsPagination, setSignupsPagination] = useState<PaginationInfo>({ total_pages: 1, total_items: 0 });
  const [loadingSignups, setLoadingSignups] = useState(false);

  const [affGroups, setAffGroups] = useState<AffiliateGroup[]>([]);
  const [affPlayers, setAffPlayers] = useState<AffiliatePlayerRow[]>([]);
  const [affPage, setAffPage] = useState(1);
  const [affPagination, setAffPagination] = useState<PaginationInfo>({ total_pages: 1, total_items: 0 });
  const [loadingAff, setLoadingAff] = useState(false);

  // Inactive Players (add-on gated). Its own controls; the page date range doesn't apply.
  const [inactiveEnabled, setInactiveEnabled] = useState(false);
  const [inactiveItems, setInactiveItems] = useState<InactivePlayerRow[]>([]);
  const [inactivePage, setInactivePage] = useState(1);
  const [inactivePagination, setInactivePagination] = useState<PaginationInfo | null>(null);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [inactiveDaysInput, setInactiveDaysInput] = useState('30');
  const [inactiveDays, setInactiveDays] = useState(30);
  const [includeNever, setIncludeNever] = useState(false);

  // Which add-ons this admin's tenant has. Failure just leaves gated sections hidden.
  useEffect(() => {
    let cancelled = false;
    api.get('/api/v1/admin/addons')
      .then((res) => {
        if (!cancelled) setInactiveEnabled(asArray<string>((res.data as any)?.enabled).includes(INACTIVE_ADDON_KEY));
      })
      .catch(() => { /* keep hidden */ });
    return () => { cancelled = true; };
  }, []);

  // Default range on mount (last 7 days, in the initial report timezone).
  useEffect(() => {
    const range = computeRange('last7', timezone);
    if (!range) return;
    setStartDate(parseDay(range.start));
    setEndDate(parseDay(range.end));
    setApplied({ start: range.start, end: range.end });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateRangeChange = (range: DateRangePreset) => {
    setDateRange(range);
    if (range === 'custom') return;
    const r = computeRange(range, timezone);
    if (!r) return;
    setStartDate(parseDay(r.start));
    setEndDate(parseDay(r.end));
  };

  // Changing the report timezone recomputes the current preset's boundary and re-applies
  // immediately (custom ranges are tz-independent, so they're left alone).
  const changeTimezone = (tz: string) => {
    setTimezone(tz);
    try { localStorage.setItem(TZ_STORAGE_KEY, tz); } catch { /* ignore */ }
    if (dateRange === 'custom') return;
    const r = computeRange(dateRange, tz);
    if (!r) return;
    setStartDate(parseDay(r.start));
    setEndDate(parseDay(r.end));
    setPlayersPage(1); setSignupsPage(1); setAffPage(1); setInactivePage(1);
    setApplied((prev) => ({ start: r.start, end: r.end, tenant: prev?.tenant }));
  };

  const applyFilters = () => {
    if (!startDate || !endDate) {
      showError('Please select a date range');
      return;
    }
    if (startDate > endDate) {
      showError('Start date must be before end date');
      return;
    }
    const tenant = isSuperAdminView
      ? (tenantFilter.trim() ? parseInt(tenantFilter.trim(), 10) : undefined)
      : tenantId;
    if (isSuperAdminView && tenantFilter.trim() && Number.isNaN(tenant as number)) {
      showError('Tenant ID must be a number');
      return;
    }
    // Reset pagination so a new range starts from page 1.
    setPlayersPage(1); setSignupsPage(1); setAffPage(1); setInactivePage(1);
    // Custom-range dates come from the DatePicker and are sent as picked (tz-independent);
    // preset-computed dates are already UTC-midnight stand-ins for the tz-local day.
    const extract = dateRange === 'custom' ? toDateParam : toDay;
    setApplied({ start: extract(startDate), end: extract(endDate), tenant });
  };

  const baseParams = useCallback(() => {
    if (!applied) return null;
    const p: Record<string, any> = { start_date: applied.start, end_date: applied.end, tz: timezone };
    if (applied.tenant) p.tenant_id = applied.tenant;
    return p;
  }, [applied, timezone]);

  // ----- Fetch overview (summary totals + time-series) -----
  useEffect(() => {
    const params = baseParams();
    if (!params) return;
    let cancelled = false;
    const run = async () => {
      setLoadingOverview(true);
      try {
        const [summaryRes, seriesRes] = await Promise.all([
          api.get('/api/v1/admin/analytics/summary', { params: { ...params, page: 1, page_size: 1 } }),
          api.get('/api/v1/admin/analytics/time-series', { params }),
        ]);
        if (cancelled) return;
        const s = summaryRes.data as any;
        const totalsObj = s && typeof s === 'object' ? (s.totals ?? s) : null;
        setTotals(totalsObj && typeof totalsObj === 'object' && !Array.isArray(totalsObj) ? totalsObj : null);
        const ts = seriesRes.data as any;
        // Backend returns the daily array under `points`; keep `series` as a fallback.
        setSeries(asArray<TimeSeriesPoint>(ts?.points, ts?.series, ts));
      } catch (error: any) {
        if (!cancelled) showError(error.response?.data?.detail || 'Failed to load analytics overview');
      } finally {
        if (!cancelled) setLoadingOverview(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [applied, baseParams, showError]);

  // ----- Fetch players (Player Breakdown tab) -----
  useEffect(() => {
    const params = baseParams();
    if (!params || tab !== 0) return;
    let cancelled = false;
    const run = async () => {
      setLoadingPlayers(true);
      try {
        const res = await api.get('/api/v1/admin/analytics/summary', {
          params: { ...params, activity, page: playersPage, page_size: PAGE_SIZE },
        });
        if (cancelled) return;
        const data = res.data as any;
        setPlayers(asArray<PlayerRow>(data?.players, data));
        if (data?.pagination) setPlayersPagination(data.pagination);
      } catch (error: any) {
        if (!cancelled) showError(error.response?.data?.detail || 'Failed to load players');
      } finally {
        if (!cancelled) setLoadingPlayers(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [applied, baseParams, tab, playersPage, activity, showError]);

  // ----- Fetch signups (New Sign-Ups tab) -----
  useEffect(() => {
    const params = baseParams();
    if (!params || tab !== 1) return;
    let cancelled = false;
    const run = async () => {
      setLoadingSignups(true);
      try {
        const res = await api.get('/api/v1/admin/analytics/signups', {
          params: { ...params, page: signupsPage, page_size: PAGE_SIZE },
        });
        if (cancelled) return;
        const data = res.data as any;
        setSignups(asArray<SignupRow>(data?.items, data));
        if (data?.pagination) setSignupsPagination(data.pagination);
      } catch (error: any) {
        if (!cancelled) showError(error.response?.data?.detail || 'Failed to load sign-ups');
      } finally {
        if (!cancelled) setLoadingSignups(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [applied, baseParams, tab, signupsPage, showError]);

  // ----- Fetch affiliate breakdown (Affiliate tab) -----
  useEffect(() => {
    const params = baseParams();
    if (!params || tab !== 2) return;
    let cancelled = false;
    const run = async () => {
      setLoadingAff(true);
      try {
        const res = await api.get('/api/v1/admin/analytics/affiliate-breakdown', {
          params: { ...params, page: affPage, page_size: PAGE_SIZE },
        });
        if (cancelled) return;
        const data = res.data as any;
        setAffGroups(asArray<AffiliateGroup>(data?.groups));
        setAffPlayers(asArray<AffiliatePlayerRow>(data?.players));
        if (data?.pagination) setAffPagination(data.pagination);
      } catch (error: any) {
        if (!cancelled) showError(error.response?.data?.detail || 'Failed to load affiliate breakdown');
      } finally {
        if (!cancelled) setLoadingAff(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [applied, baseParams, tab, affPage, showError]);

  // ----- Fetch inactive players (Inactive Players tab, add-on gated) -----
  const appliedTenant = applied?.tenant;
  useEffect(() => {
    if (!inactiveEnabled || tab !== INACTIVE_TAB) return;
    let cancelled = false;
    const run = async () => {
      setLoadingInactive(true);
      try {
        const params: Record<string, any> = {
          days: inactiveDays,
          include_never_purchased: includeNever,
          page: inactivePage,
          page_size: INACTIVE_PAGE_SIZE,
        };
        if (appliedTenant) params.tenant_id = appliedTenant;
        const res = await api.get('/api/v1/admin/analytics/inactive-players', { params });
        if (cancelled) return;
        const data = res.data as any;
        setInactiveItems(asArray<InactivePlayerRow>(data?.items));
        if (data?.pagination) setInactivePagination(data.pagination);
      } catch (error: any) {
        if (cancelled) return;
        if (error.response?.status === 403) {
          // Add-on was switched off for this tenant; hide the section.
          setInactiveEnabled(false);
          setTab(0);
        }
        showError(error.response?.data?.detail || 'Failed to load inactive players');
      } finally {
        if (!cancelled) setLoadingInactive(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [inactiveEnabled, tab, appliedTenant, inactiveDays, includeNever, inactivePage, showError]);

  const applyInactiveDays = () => {
    const n = Number(inactiveDaysInput);
    if (!Number.isInteger(n) || n < 1 || n > 3650) {
      showError('Days inactive must be a whole number between 1 and 3650');
      return;
    }
    setInactivePage(1);
    setInactiveDays(n);
  };

  const openExport = (dataset: DatasetKey) => {
    setExportDataset(dataset);
    setExportOpen(true);
  };

  const handleEmailClick = (email: string) => {
    navigate(`/users?search=${encodeURIComponent(email)}`);
  };

  // Only super admins send tenant_id for payouts; tenant admins are scoped by their token.
  const payoutTenantId = isSuperAdminView
    ? applied?.tenant
    : (isAuthenticatedSuperAdmin() ? tenantId : undefined);

  const affLabel = (id: string | null) => (id === null || id === '' ? 'Unassigned' : id);

  const tzAbbr = TIMEZONE_OPTIONS.find((o) => o.value === timezone)?.abbr ?? timezone;
  const playersColumnCount = 9 + (activity !== 'purchases' ? 2 : 0);

  // Chart palette from the MUI theme so it adapts to light/dark.
  const colors = {
    ggr: theme.palette.success.main,
    purchases: theme.palette.primary.main,
    redeems: theme.palette.error.main,
    signups: theme.palette.info.main,
    grid: theme.palette.divider,
    axis: theme.palette.text.secondary,
  };

  const chartTooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" gutterBottom>Analytics Dashboard</Typography>

        {/* Controls */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => handleDateRangeChange(e.target.value as DateRangePreset)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last7">Last 7 days</MenuItem>
                <MenuItem value="lastWeek">Last week (Mon–Sun)</MenuItem>
                <MenuItem value="month">This month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 170 }} size="small">
              <InputLabel>Timezone</InputLabel>
              <Select
                value={timezone}
                label="Timezone"
                onChange={(e) => changeTimezone(e.target.value as string)}
              >
                {TIMEZONE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {dateRange === 'custom' && (
              <>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(d) => setStartDate(d)}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(d) => setEndDate(d)}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
                />
              </>
            )}

            {isSuperAdminView && (
              <TextField
                label="Tenant ID (optional)"
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                placeholder="All tenants"
                size="small"
                sx={{ minWidth: 160 }}
              />
            )}

            <Button variant="contained" onClick={applyFilters}>Apply</Button>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => openExport('signups')}
              disabled={!applied}
            >
              Download CSV
            </Button>
          </Box>
        </Paper>

        {applied && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {applied.start === applied.end
              ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(parseDay(applied.start))
              : `${applied.start} – ${applied.end}`}
            {' · '}{TIMEZONE_OPTIONS.find((o) => o.value === timezone)?.label ?? timezone}
          </Typography>
        )}

        {/* KPI tiles */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {[
            { label: 'Total Purchases', value: totals ? formatCurrency(totals.total_purchases) : '—', color: colors.purchases },
            { label: 'Total Redeems', value: totals ? formatCurrency(totals.total_redeems) : '—', color: colors.redeems },
            { label: 'Net GGR', value: totals ? formatCurrency(totals.total_ggr) : '—', color: colors.ggr },
            { label: 'Distinct Purchasers', value: totals ? totals.distinct_purchasers.toLocaleString() : '—', color: colors.signups },
          ].map((kpi) => (
            <Card key={kpi.label} sx={{ flex: '1 1 220px', minWidth: 200 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">{kpi.label}</Typography>
                <Typography variant="h4" sx={{ color: kpi.color }}>
                  {loadingOverview ? <CircularProgress size={28} /> : kpi.value}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Charts */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Paper sx={{ p: 2, flex: '1 1 480px', minWidth: 320 }}>
            <Typography variant="subtitle1" gutterBottom>Net GGR per Day</Typography>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Box sx={{ minWidth: 320, height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                    <XAxis dataKey="date" tick={{ fill: colors.axis, fontSize: 12 }} />
                    <YAxis tick={{ fill: colors.axis, fontSize: 12 }} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={(v: any) => formatCurrency(Number(v))} />
                    <Line type="monotone" dataKey="ggr" name="Net GGR" stroke={colors.ggr} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 480px', minWidth: 320 }}>
            <Typography variant="subtitle1" gutterBottom>Purchases vs Redeems per Day</Typography>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Box sx={{ minWidth: 320, height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                    <XAxis dataKey="date" tick={{ fill: colors.axis, fontSize: 12 }} />
                    <YAxis tick={{ fill: colors.axis, fontSize: 12 }} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={(v: any) => formatCurrency(Number(v))} />
                    <Legend />
                    <Bar dataKey="purchases" name="Purchases" fill={colors.purchases} />
                    <Bar dataKey="redeems" name="Redeems" fill={colors.redeems} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, flex: '1 1 480px', minWidth: 320 }}>
            <Typography variant="subtitle1" gutterBottom>New Sign-Ups per Day</Typography>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <Box sx={{ minWidth: 320, height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                    <XAxis dataKey="date" tick={{ fill: colors.axis, fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fill: colors.axis, fontSize: 12 }} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Bar dataKey="signups" name="Sign-Ups" fill={colors.signups} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Paper>
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: -2, mb: 2 }}>
          Charts above are bucketed by UTC days.
        </Typography>

        {/* Tabbed tables */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Player Breakdown" />
            <Tab label="New Sign-Ups" />
            <Tab label="Affiliate" />
            <Tab value={PAYOUTS_TAB} label="Affiliate Payouts" />
            {inactiveEnabled && (
              <Tab
                value={INACTIVE_TAB}
                label={`Inactive Players${inactivePagination ? ` (${inactivePagination.total_items.toLocaleString()})` : ''}`}
              />
            )}
          </Tabs>

          <Box sx={{ p: 2 }}>
            {/* Player Breakdown */}
            {tab === 0 && (
              loadingPlayers ? <CircularProgress /> : (
                <>
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={activity}
                      onChange={(_e, v: ActivityFilter | null) => { if (v) { setActivity(v); setPlayersPage(1); } }}
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="purchases">Purchases only</ToggleButton>
                      <ToggleButton value="redeems">Redeems only</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Username</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Affiliate ID</TableCell>
                          <TableCell align="right">Purchases</TableCell>
                          <TableCell align="right"># Purchases</TableCell>
                          <TableCell>First purchase ({tzAbbr})</TableCell>
                          <TableCell>Last purchase ({tzAbbr})</TableCell>
                          {activity !== 'purchases' && <TableCell align="right">Redeems</TableCell>}
                          {activity !== 'purchases' && <TableCell align="right">Individual GGR</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {players.map((p) => (
                          <TableRow key={p.user_id}>
                            <TableCell>
                              <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                                onClick={() => handleEmailClick(p.email)}>
                                {p.email}
                              </Button>
                            </TableCell>
                            <TableCell>{p.username || '—'}</TableCell>
                            <TableCell>{fullName(p.first_name, p.last_name)}</TableCell>
                            <TableCell>{p.phone_number || '—'}</TableCell>
                            <TableCell>{affLabel(p.affiliate_id)}</TableCell>
                            <TableCell align="right">{formatCurrency(p.purchases)}</TableCell>
                            <TableCell align="right">{(p.purchase_count ?? 0).toLocaleString()}</TableCell>
                            <TableCell>{formatInTz(p.first_purchase_at, timezone)}</TableCell>
                            <TableCell>{formatInTz(p.last_purchase_at, timezone)}</TableCell>
                            {activity !== 'purchases' && <TableCell align="right">{formatCurrency(p.redeems)}</TableCell>}
                            {activity !== 'purchases' && <TableCell align="right">{formatCurrency(p.individual_ggr)}</TableCell>}
                          </TableRow>
                        ))}
                        {!players.length && (
                          <TableRow><TableCell colSpan={playersColumnCount}>No player activity in this range.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {playersPagination.total_pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination count={playersPagination.total_pages} page={playersPage}
                        onChange={(_e, v) => setPlayersPage(v)} color="primary" />
                    </Box>
                  )}
                </>
              )
            )}

            {/* New Sign-Ups */}
            {tab === 1 && (
              loadingSignups ? <CircularProgress /> : (
                <>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 720 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Affiliate ID</TableCell>
                          <TableCell>Signed Up</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {signups.map((s) => (
                          <TableRow key={s.id}>
                            <TableCell>
                              <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                                onClick={() => handleEmailClick(s.email)}>
                                {s.email}
                              </Button>
                            </TableCell>
                            <TableCell>{[s.first_name, s.last_name].filter(Boolean).join(' ') || '-'}</TableCell>
                            <TableCell>{affLabel(s.affiliate_id)}</TableCell>
                            <TableCell>{formatEst(s.created_at)}</TableCell>
                          </TableRow>
                        ))}
                        {!signups.length && (
                          <TableRow><TableCell colSpan={4}>No sign-ups in this range.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {signupsPagination.total_pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination count={signupsPagination.total_pages} page={signupsPage}
                        onChange={(_e, v) => setSignupsPage(v)} color="primary" />
                    </Box>
                  )}
                </>
              )
            )}

            {/* Affiliate */}
            {tab === 2 && (
              loadingAff ? <CircularProgress /> : (
                <>
                  <Typography variant="subtitle1" gutterBottom>Group Totals by Affiliate</Typography>
                  <TableContainer sx={{ overflowX: 'auto', mb: 3 }}>
                    <Table size="small" sx={{ minWidth: 820 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Affiliate ID</TableCell>
                          <TableCell align="right">Players</TableCell>
                          <TableCell align="right">Purchases</TableCell>
                          <TableCell align="right">Redeems</TableCell>
                          <TableCell align="right">Net GGR</TableCell>
                          <TableCell align="right">Affiliate GGR</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {affGroups.map((g) => (
                          <TableRow key={g.affiliate_id ?? 'unassigned'}>
                            <TableCell>{affLabel(g.affiliate_id)}</TableCell>
                            <TableCell align="right">{g.player_count.toLocaleString()}</TableCell>
                            <TableCell align="right">{formatCurrency(g.total_purchases)}</TableCell>
                            <TableCell align="right">{formatCurrency(g.total_redeems)}</TableCell>
                            <TableCell align="right">{formatCurrency(g.net_ggr)}</TableCell>
                            <TableCell align="right">{formatCurrency(g.affiliate_ggr)}</TableCell>
                          </TableRow>
                        ))}
                        {!affGroups.length && (
                          <TableRow><TableCell colSpan={6}>No affiliate activity in this range.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="subtitle1" gutterBottom>Players with Purchases (by Affiliate)</Typography>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 720 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Affiliate ID</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell align="right">Purchases</TableCell>
                          <TableCell align="right">Redeems</TableCell>
                          <TableCell align="right">Net GGR</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {affPlayers.map((p) => (
                          <TableRow key={p.user_id}>
                            <TableCell>{affLabel(p.affiliate_id)}</TableCell>
                            <TableCell>
                              <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                                onClick={() => handleEmailClick(p.email)}>
                                {p.email}
                              </Button>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(p.purchases)}</TableCell>
                            <TableCell align="right">{formatCurrency(p.redeems)}</TableCell>
                            <TableCell align="right">{formatCurrency(p.net_ggr)}</TableCell>
                          </TableRow>
                        ))}
                        {!affPlayers.length && (
                          <TableRow><TableCell colSpan={5}>No purchasers in this range.</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {affPagination.total_pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination count={affPagination.total_pages} page={affPage}
                        onChange={(_e, v) => setAffPage(v)} color="primary" />
                    </Box>
                  )}
                </>
              )
            )}

            {/* Affiliate Payouts (free for every tenant; own period controls) */}
            {tab === PAYOUTS_TAB && (
              <AffiliatePayoutsSection
                tenantId={payoutTenantId}
                showTenantColumn={isSuperAdminView}
                onError={showError}
              />
            )}

            {/* Inactive Players (add-on) */}
            {inactiveEnabled && tab === INACTIVE_TAB && (
              <>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                  <TextField
                    label="Days inactive"
                    type="number"
                    size="small"
                    value={inactiveDaysInput}
                    onChange={(e) => setInactiveDaysInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyInactiveDays(); }}
                    inputProps={{ min: 1, max: 3650, step: 1 }}
                    sx={{ width: 140 }}
                  />
                  <Button variant="contained" onClick={applyInactiveDays}>Update</Button>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeNever}
                        onChange={(e) => { setInactivePage(1); setIncludeNever(e.target.checked); }}
                      />
                    }
                    label="Include players who never purchased"
                  />
                  <Box sx={{ flexGrow: 1 }} />
                  <Button variant="outlined" startIcon={<DownloadIcon />}
                    onClick={() => openExport('inactive_players')} disabled={!applied}>
                    Download CSV
                  </Button>
                </Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This report ignores the date range above. It lists players with no purchase in the
                  last {inactiveDays} days{includeNever ? ', plus players who never purchased' : ''}
                  {appliedTenant ? ` · Tenant ${appliedTenant}` : ''}.
                </Alert>
                {loadingInactive ? <CircularProgress /> : (
                  <>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 1000 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Last Purchase</TableCell>
                            <TableCell align="right">Days Since</TableCell>
                            <TableCell align="right">Lifetime Purchases</TableCell>
                            <TableCell align="right">Credit Balance</TableCell>
                            <TableCell>Affiliate</TableCell>
                            <TableCell align="right">GGR</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {inactiveItems.map((p) => (
                            <TableRow key={p.user_id}>
                              <TableCell>{fullName(p.first_name, p.last_name)}</TableCell>
                              <TableCell>
                                {p.email ? (
                                  <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                                    onClick={() => handleEmailClick(p.email as string)}>
                                    {p.email}
                                  </Button>
                                ) : '—'}
                              </TableCell>
                              <TableCell>{p.phone_number || '—'}</TableCell>
                              <TableCell>{p.last_purchase_at ? formatEst(p.last_purchase_at) : 'Never'}</TableCell>
                              <TableCell align="right">{p.days_since_purchase ?? 'Never'}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(p.lifetime_purchases)}
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {(p.lifetime_purchase_count || 0).toLocaleString()} purchase{p.lifetime_purchase_count === 1 ? '' : 's'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">{formatCurrency(p.credit_balance)}</TableCell>
                              <TableCell>{p.affiliate_id || '—'}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(p.lifetime_ggr)}
                                <Typography variant="caption" color="textSecondary" display="block">
                                  {formatCurrency(p.lifetime_redeems)} redeemed
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {!inactiveItems.length && (
                            <TableRow><TableCell colSpan={9}>No inactive players for this threshold.</TableCell></TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {inactivePagination && inactivePagination.total_pages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination count={inactivePagination.total_pages} page={inactivePage}
                          onChange={(_e, v) => setInactivePage(v)} color="primary" />
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </Paper>

        {applied && (
          <AnalyticsExportDialog
            open={exportOpen}
            onClose={() => setExportOpen(false)}
            startDate={applied.start}
            endDate={applied.end}
            tenantId={applied.tenant}
            onError={showError}
            initialDataset={exportDataset}
            inactiveDays={inactiveDays}
            includeNeverPurchased={includeNever}
            tz={timezone}
            activity={activity}
          />
        )}

        <Snackbar open={snackbar.open} autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default AnalyticsDashboardPage;
